import { defineModel } from "../define-model";

/** Mirrors `api/src/agents/types.ts`. */
export type Autonomy = "suggest" | "approve" | "auto";
export type ToolRisk = "read" | "draft" | "outward" | "spend";

export type AgentToolPolicy = {
	/** Tool names the agent may call; empty = its template's defaults. */
	allow: string[];
	/** Per-tool autonomy replacing the agent's for that tool. */
	overrides: Record<string, Autonomy>;
};

export type AgentEventFilter = {
	platforms?: ("instagram" | "tiktok" | "facebook" | "threads" | "x")[];
	socialAccountIds?: string[];
	sources?: ("manual" | "flow" | "assistant" | "agent" | "content_plan" | "imported")[];
	metric?:
		| "impressions"
		| "reach"
		| "views"
		| "likes"
		| "comments"
		| "shares"
		| "saves"
		| "clicks"
		| "engagementRate";
	op?: "gte" | "lte";
	value?: number;
	sentiments?: ("positive" | "neutral" | "negative" | "mixed")[];
	keywords?: string[];
	includeReplies?: boolean;
	hours?: number;
};

export type AgentTrigger =
	| { key: string; kind: "schedule"; cron: string; timezone: string; prompt?: string }
	| { key: string; kind: "event"; eventType: string; filter: AgentEventFilter; prompt?: string };

/** Millicredits (1 credit = 1000); null = no cap. */
export type AgentBudget = { dailyMc: number | null; monthlyMc: number | null };

export type AgentMemoryNote = { id: string; text: string; createdAt: string };

export type Agent = {
	id: string;
	workspaceId: string;
	projectId: string;
	name: string;
	/** `custom` or a key from `GET /agents/templates`. */
	template: string;
	description: string;
	goal: string;
	instructions: string;
	/** An assistant model id; null = the default. */
	model: string | null;
	autonomy: Autonomy;
	toolPolicy: AgentToolPolicy;
	flowIds: string[];
	triggers: AgentTrigger[];
	budget: AgentBudget;
	memory: { notes: AgentMemoryNote[] };
	maxSteps: number;
	status: "active" | "paused";
	lastRunAt: string | null;
	/** Runs act with this member's permissions (∩ the allowlist). */
	createdById: string | null;
	archivedAt: string | null;
	createdAt: string;
	updatedAt: string;
};

declare module "../types" {
	interface SyncModels {
		agent: Agent;
	}
}

export const model = defineModel({
	name: "agent",
	load: "eager",
	indexes: ["projectId"],
	defaults: ({ userId, workspaceId, now }) => ({
		workspaceId,
		template: "custom",
		description: "",
		goal: "",
		instructions: "",
		model: null,
		autonomy: "approve" as const,
		toolPolicy: { allow: [], overrides: {} },
		flowIds: [],
		triggers: [],
		budget: { dailyMc: null, monthlyMc: null },
		memory: { notes: [] },
		maxSteps: 12,
		status: "active" as const,
		lastRunAt: null,
		createdById: userId,
		archivedAt: null,
		createdAt: now,
		updatedAt: now,
	}),
	sort: (a, b) => a.name.localeCompare(b.name) || a.createdAt.localeCompare(b.createdAt),
});
